/* TODO: investigate using '@vue/cli-plugin-babel/preset' in babel.config.js
 * so that we can use ?. and ?? here.
 */

import { getStartingLocale } from './env'

/**
 * Get domain of current window
 */
export function getDomain() {
  const parts = window.location.hostname.split('.')
  const { length } = parts
  return length < 2 ? window.location.hostname : `${parts[length - 2]}.${parts[length - 1]}`
}

/**
 * Get the current stage from environment variables
 *
 * @returns {string}
 */
export const getStage = (): string => (process.env.VUE_APP_STAGE || process.env.NODE_ENV || 'dev')

/**
 * Get the current region from environment variables
 *
 * @returns {string}
 */
export const getRegion = (): string => (process?.env?.VUE_APP_REGION || 'us')

type KeyString = {
  [key: string]: string
}

/**
 * Enum for apps
 */
export const APP_NAMES: KeyString = {
  auth: 'auth',
  care: 'care',
  exams: 'exams'
}

/**
 * Enum for VUE_APP_STAGE options
 */
export const STAGE_OPTIONS: KeyString = {
  dev: 'dev',
  local: 'local',
  prod: 'prod',
  qa: 'qa',
  staging: 'staging',
  usprod: 'usprod'
}

/**
 * Enum for VUE_APP_REGION options
 */
export const REGION_OPTIONS = {
  jp: 'jp',
  us: 'us'
}

/**
 * Urls for global web
 */
const globalAppBaseUrls = {
  [APP_NAMES.auth]: 'https://{stage}app.neurotrack.com/',
  [APP_NAMES.care]: 'https://{stage}care.neurotrack.com/',
  [APP_NAMES.exams]: 'https://{stage}exams.neurotrack.io/'
}

/**
 * Get the app url given the app name, and optionally, the stage
 *
 * @param {APP_NAMES} app The name of the app to fetch the url for
 * @param {string} stage (optional) The name of the stage. Default: current stage set in .env.
 * @returns {string} The appropriate app url, or empty string if something doesn't exist
 */
export const getAppUrl = (
  app: string,
  stage: string = getStage()
): string => {
  // no stage prefix needed if prod or usprod
  stage = ((stage === STAGE_OPTIONS.prod) || (stage === STAGE_OPTIONS.usprod)) ? '' : `${stage}.`
  return (globalAppBaseUrls[app] || '').replace('{stage}', stage)
}

/**
 * DEPRECATED: retaining for possible future needs
 * URL hash for API in dev and jp prod stages.
 * Because multiple stage options use the same url, this hash reduces repetition.
 * dev stages: dev, devlopment, local
 * prod stages: prod, production
const apiMultiKeyHash = {
  [STAGE_OPTIONS.dev]: {
    [REGION_OPTIONS.jp]: 'https://api.dev.jp.neurotrack.io/',
    [REGION_OPTIONS.us]: 'https://api.dev-neurotrack.io/'
  }
}
 */

/**
 * DEPRECATED: retaining for possible future needs
 * Enum for returning the correct API url, depending on stage and environment.
 
const apiUrlHash = {
  [STAGE_OPTIONS.dev]: apiMultiKeyHash[STAGE_OPTIONS.dev],
  [STAGE_OPTIONS.prod]: 'https://api.jp-neurotrack.io/',
  [STAGE_OPTIONS.qa]: {
    [REGION_OPTIONS.jp]: 'https://api.qa.jp.neurotrack.io/',
    [REGION_OPTIONS.us]: 'https://api.qa-neurotrack.io/'
  },
  [STAGE_OPTIONS.staging]: {
    [REGION_OPTIONS.jp]: 'https://api.staging.jp.neurotrack.io/',
    [REGION_OPTIONS.us]: 'https://api.staging-neurotrack.io/'
  },
  [STAGE_OPTIONS.usprod]: {
    [REGION_OPTIONS.us]: 'https://api.us-neurotrack.io/'
  }
}
 */

/**
 * DEPRECATED: retaining for possible future needs
 * Get the API url, optionally given the stage and region
 *
 * @param {string} stage (optional) The name of the stage. Default: the current stage in .env.
 * @param {string} region (optional) The name of the region. Default: the current region in .env.
 * @returns {string} The appropriate API url, or empty string if something doesn't exist
export const getApiUrl = (stage = getStage(), region = getRegion()) => {
  // if the stage object doesn't exist, don't bother
  const stageObj = apiUrlHash[stage]
  if (!stageObj) {
    return ''
  }
 
  // some stages don't have all the regions
  const url = stageObj[region]
  if (url) { return url }
 
  // the specified region doesn't exist in the stage, so return the first region available
  const keys = Object.keys(stageObj)
  return keys.length < 1 ? '' : stageObj[keys[0]]
}
 */

// #region Auth

/**
 * URL hash for auth in dev and prod stages.
 */
const authMultiKeyHash = {
  [STAGE_OPTIONS.dev]: 'https://dev.auth.neurotrack.io/',
  [STAGE_OPTIONS.prod]: 'https://auth.neurotrack.io/'
}

/**
 * Enum for returning the correct global auth url depending on stage.
 */
const globalAuthHash = {
  [STAGE_OPTIONS.dev]: authMultiKeyHash[STAGE_OPTIONS.dev],
  [STAGE_OPTIONS.local]: authMultiKeyHash[STAGE_OPTIONS.dev],
  [STAGE_OPTIONS.qa]: 'https://qa.auth.neurotrack.io/',
  [STAGE_OPTIONS.staging]: 'https://staging.auth.neurotrack.io/',
  [STAGE_OPTIONS.usprod]: authMultiKeyHash[STAGE_OPTIONS.prod],
  [STAGE_OPTIONS.prod]: authMultiKeyHash[STAGE_OPTIONS.prod]
}

export const globalAuthBaseURL = globalAuthHash[getStage() === 'development' ? 'dev' : getStage()]
// #endregion Auth

// #region OAUTH Authentication
/**
 * URL hash for OAUTH authentication in dev and prod stages.
 */
const oauthMultiKeyHash = {
  [STAGE_OPTIONS.dev]: 'https://myidentity-support.platform.athenahealth.com/',
  [STAGE_OPTIONS.usprod]: 'https://myidentity.platform.athenahealth.com/',
  [STAGE_OPTIONS.prod]: 'https://myidentity.platform.athenahealth.com/'
}

/**
 * Enum for returning the correct OAUTH url depending on stage.
 */
const globalOauthHash = {
  [STAGE_OPTIONS.dev]: oauthMultiKeyHash[STAGE_OPTIONS.dev],
  [STAGE_OPTIONS.local]: oauthMultiKeyHash[STAGE_OPTIONS.dev],
  [STAGE_OPTIONS.qa]: oauthMultiKeyHash[STAGE_OPTIONS.dev],
  [STAGE_OPTIONS.staging]: oauthMultiKeyHash[STAGE_OPTIONS.dev],
  [STAGE_OPTIONS.usprod]: oauthMultiKeyHash[STAGE_OPTIONS.usprod],
  [STAGE_OPTIONS.prod]: oauthMultiKeyHash[STAGE_OPTIONS.prod]
}

export const globalOauthBaseURL = globalOauthHash[getStage()]
// #endregion OAUTH

// #region Terms and Privacy

/**
 * (Similar to apiMultiKeyHash) URL hash for My in dev and jp prod stages.
 */
const myMultiKeyHash = {
  [STAGE_OPTIONS.dev]: {
    [REGION_OPTIONS.jp]: 'https://my.dev.jp.neurotrack.com/',
    [REGION_OPTIONS.us]: 'https://my.dev.us.neurotrack.com/'
  }
}

/**
 * Enum for returning the correct My url, depending on stage and environment.
 */
const myUrlHash = {
  [STAGE_OPTIONS.dev]: myMultiKeyHash[STAGE_OPTIONS.dev],
  [STAGE_OPTIONS.local]: myMultiKeyHash[STAGE_OPTIONS.dev],
  [STAGE_OPTIONS.qa]: {
    [REGION_OPTIONS.jp]: 'https://my.qa.jp.neurotrack.com/',
    [REGION_OPTIONS.us]: 'https://my.qa.us.neurotrack.com/'
  },
  [STAGE_OPTIONS.staging]: {
    [REGION_OPTIONS.jp]: 'https://my.staging.jp.neurotrack.com/',
    [REGION_OPTIONS.us]: 'https://my.staging.us.neurotrack.com/'
  },
  [STAGE_OPTIONS.usprod]: {
    [REGION_OPTIONS.jp]: 'https://my.jp.neurotrack.com/',
    [REGION_OPTIONS.us]: 'https://my.us.neurotrack.com/'
  }
}

const languageHash = {
  [REGION_OPTIONS.jp]: 'ja',
  [REGION_OPTIONS.us]: 'en'
}
/**
 * Get the url with appended language for the my-nt app.
 *
 * @returns {string}
 */
const getMyLangUrl = () => {
  /**
   * This will mainly be used on the registration page for Terms and Privacy links.
   * Since we won't have regioned app urls anymore, we will use the starting locale as the region.
   */
  const stage = getStage()
  const language = getStartingLocale()
  // get region from the language; default to 'us'
  const region: string = Object.keys(languageHash)
    .find(key => languageHash[key] === language)
    || REGION_OPTIONS.us

  return myUrlHash[stage][region] + language
}

/**
 * Get the region's Terms of Service url
 *
 * @returns {string} The region's Terms of Service url
 */
export const getTermsUrl = (): string => `${getMyLangUrl()}/terms`

/**
 * Get the region's Privacy url
 *
 * @returns {string} The region's Privacy Policy url
 */
export const getPrivacyUrl = (): string => `${getMyLangUrl()}/privacy`
// #endregion Terms and Privacy
